import React, {useCallback, useEffect, useState} from 'react'
import {Stack, Typography} from '@mui/material'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'

import {colors} from '@/constants'
import HoverButton from '@/components/UI/button/HoverButton'
import SimpleLoader from '@/components/UI/simpleLoader'
import TransCustom from '@/components/UI/i18next'
import {useTranslation} from 'react-i18next'
import {errorCodeHandler} from '@/helpers/localErrorCodeHandler'
import {isErrorResponse} from '@/helpers/IsErrorResponse'
import SupportService from '@/services/api/Support'
import {
  formatTicketDate,
  normalizeTickets,
  SupportTicketItem,
} from '@/helpers/supportTickets'
import NewTicketForm from './NewTicketForm'
import StatusBadge from './StatusBadge'
import TicketThread from './TicketThread'

const TicketRow = ({
  ticket,
  onClick,
}: {
  ticket: SupportTicketItem
  onClick: () => void
}) => {
  return (
    <Stack
      className="pointer"
      onClick={onClick}
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      gap="16px"
      padding="14px 16px"
      boxSizing="border-box"
      sx={{
        border: `1px solid ${colors.lightBrown}40`,
        borderRadius: '10px',
        transition: 'border-color 0.2s ease, background 0.2s ease',
        '&:hover': {borderColor: colors.orange, background: '#37281980'},
      }}>
      <Stack minWidth={0} gap="4px">
        <Stack direction="row" alignItems="center" gap="8px" minWidth={0}>
          {ticket.hasUnread && (
            <Stack
              flex="none"
              width="8px"
              height="8px"
              borderRadius="50%"
              sx={{background: colors.orange}}
            />
          )}
          <Typography
            color={colors.white}
            fontSize="16px"
            fontWeight={ticket.hasUnread ? '600' : '500'}
            noWrap>
            {ticket.subject}
          </Typography>
        </Stack>
        {ticket.lastMessage && (
          <Typography color={colors.gray} fontSize="13px" noWrap>
            {ticket.lastMessage}
          </Typography>
        )}
      </Stack>
      <Stack alignItems="flex-end" gap="4px" flex="none">
        <StatusBadge status={ticket.status} />
        <Typography color={colors.gray} fontSize="12px">
          {formatTicketDate(ticket.updatedAt)}
        </Typography>
      </Stack>
    </Stack>
  )
}

const Support = () => {
  const {t} = useTranslation()
  const [tickets, setTickets] = useState<SupportTicketItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [selected, setSelected] = useState<SupportTicketItem | null>(null)
  const [isCreating, setIsCreating] = useState(false)

  const load = useCallback(async () => {
    setIsLoading(true)
    try {
      const response = await SupportService.getTickets()
      if (isErrorResponse(response.data)) {
        errorCodeHandler(response.data)
        return
      }
      setTickets(normalizeTickets(response.data))
    } catch (e) {
      errorCodeHandler(e)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const content = () => {
    if (selected) {
      return (
        <TicketThread
          ticket={selected}
          onBack={() => {
            setSelected(null)
            void load()
          }}
        />
      )
    }

    if (isCreating) {
      return (
        <NewTicketForm
          onCancel={() => setIsCreating(false)}
          onCreated={() => {
            setIsCreating(false)
            void load()
          }}
        />
      )
    }

    return (
      <Stack flex="auto" minHeight={0} gap="16px" marginTop="20px">
        <Stack direction="row" justifyContent="flex-end" flex="none">
          <HoverButton
            onClick={() => setIsCreating(true)}
            fontSize="15px"
            fontWeight="400"
            height="42px"
            padding="10px 24px"
            gap="10px"
            textColor={colors.white}
            background={colors.brown}
            bord={`1px solid ${colors.orange}`}>
            <AddCircleOutlineIcon
              style={{color: colors.orange, fontSize: '20px'}}
            />
            {t('1736')}
          </HoverButton>
        </Stack>

        {isLoading ? (
          <SimpleLoader isLoad={true} local={true} />
        ) : tickets.length === 0 ? (
          <Stack flex="auto" alignItems="center" justifyContent="center">
            <TransCustom
              i18nKey="1740"
              color={colors.gray}
              fontSize="16px"
              textAlign="center"
            />
          </Stack>
        ) : (
          <Stack
            className="events-block"
            flex="auto"
            minHeight={0}
            overflow="auto"
            gap="12px"
            padding="0 16px 0 0"
            boxSizing="border-box"
            sx={{scrollbarGutter: 'stable'}}>
            {tickets.map((ticket) => (
              <TicketRow
                key={ticket.id}
                ticket={ticket}
                onClick={() => setSelected(ticket)}
              />
            ))}
          </Stack>
        )}
      </Stack>
    )
  }

  return (
    <Stack height="100%" width="80%" minHeight={0}>
      <TransCustom i18nKey="1735" variant="h1" color={colors.yellow} />
      {content()}
    </Stack>
  )
}

export default Support
